import * as fs from 'fs/promises';
import * as path from 'path';
import { cosmiconfig } from 'cosmiconfig';
import { emitDiagnostic } from '@json-to-office/jto-ops';

export interface PluginConfig {
  plugins?: string[];
  pluginDirs?: string[];
  autoDiscover?: boolean;
  aliases?: Record<string, string>;
  theme?: string | any;
  themePath?: string;
  discovery?: {
    maxDepth?: number;
    includeNodeModules?: boolean;
    upstreamOnly?: boolean;
    downstreamOnly?: boolean;
  };
  validation?: {
    strict?: boolean;
    allowUnknownFields?: boolean;
  };
}

/**
 * Drop undefined-valued keys. Callers build the options object from CLI flags,
 * so an absent flag arrives as `undefined` and would otherwise erase the
 * corresponding config-file value when spread.
 */
function defined<T extends object>(source: T | undefined): T {
  if (!source) return {} as T;
  return Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== undefined)
  ) as T;
}

export class PluginConfigService {
  private static instance: PluginConfigService;
  private config: PluginConfig | null = null;
  private configPath: string | null = null;

  private static readonly CONFIG_FILES = [
    '.json-to-office.config.json',
    '.json-to-office.config.js',
    'json-to-office.config.json',
    'json-to-office.config.js',
    '.json-to-officerc',
    '.json-to-officerc.json',
    '.json-to-officerc.js',
    // Legacy support
    '.json-to-docx.config.json',
    '.json-to-docx.config.js',
    'json-to-docx.config.json',
    '.json-to-pptx.config.json',
    '.json-to-pptx.config.js',
    'json-to-pptx.config.json',
  ];

  private constructor() {}

  static getInstance(): PluginConfigService {
    if (!PluginConfigService.instance) {
      PluginConfigService.instance = new PluginConfigService();
    }
    return PluginConfigService.instance;
  }

  async loadConfig(startPath?: string): Promise<PluginConfig | null> {
    try {
      const explorer = cosmiconfig('json-to-office', {
        searchPlaces: PluginConfigService.CONFIG_FILES,
        stopDir: path.parse(startPath || process.cwd()).root,
      });

      const result = await explorer.search(startPath);

      if (result) {
        this.config = result.config as PluginConfig;
        this.configPath = result.filepath;
        return this.config;
      }
    } catch (error: any) {
      emitDiagnostic(
        `Failed to load configuration: ${error.message}`,
        'warning'
      );
    }

    const packageConfig = await this.loadFromPackageJson(startPath);
    if (packageConfig) {
      this.config = packageConfig;
      return this.config;
    }

    return null;
  }

  private async loadFromPackageJson(
    startPath?: string
  ): Promise<PluginConfig | null> {
    try {
      const packagePath = path.join(startPath || process.cwd(), 'package.json');
      const packageContent = await fs.readFile(packagePath, 'utf-8');
      const packageJson = JSON.parse(packageContent);

      // Check for json-to-office, json-to-docx, or json-to-pptx keys
      const config =
        packageJson['json-to-office'] ||
        packageJson['json-to-docx'] ||
        packageJson['json-to-pptx'];

      if (config) {
        this.configPath = packagePath;
        return config as PluginConfig;
      }
    } catch {}

    return null;
  }

  getConfig(): PluginConfig | null {
    return this.config;
  }

  getConfigPath(): string | null {
    return this.configPath;
  }

  /**
   * `theme` and `themePath` are two spellings of one decision, and `themePath`
   * is the one resolved first — so merging them key-by-key would let a
   * config-file `themePath` outrank an explicit `--theme`. They merge as a
   * group instead: either flag supersedes both config-file keys, and with
   * neither flag the config file keeps both. This method is the only place
   * that sees CLI and config-file origins side by side, so precedence between
   * the two is settled here rather than downstream.
   */
  private mergeThemeSelection(
    base: PluginConfig,
    options: Partial<PluginConfig>
  ): Pick<PluginConfig, 'theme' | 'themePath'> {
    const requestedByFlag =
      options.theme !== undefined || options.themePath !== undefined;
    return requestedByFlag
      ? { theme: options.theme, themePath: options.themePath }
      : { theme: base.theme, themePath: base.themePath };
  }

  mergeWithOptions(options: Partial<PluginConfig>): PluginConfig {
    const base = this.config || {};

    return {
      ...base,
      ...defined(options),
      ...this.mergeThemeSelection(base, options),
      discovery: {
        ...base.discovery,
        ...defined(options.discovery),
      },
      validation: {
        ...base.validation,
        ...defined(options.validation),
      },
      plugins: this.mergeArrays(base.plugins, options.plugins),
      pluginDirs: this.mergeArrays(base.pluginDirs, options.pluginDirs),
      aliases: {
        ...base.aliases,
        ...defined(options.aliases),
      },
    };
  }

  private mergeArrays(arr1?: string[], arr2?: string[]): string[] | undefined {
    if (!arr1 && !arr2) return undefined;
    if (!arr1) return arr2;
    if (!arr2) return arr1;
    return Array.from(new Set([...arr1, ...arr2]));
  }

  resolveAlias(name: string): string {
    if (this.config?.aliases && this.config.aliases[name]) {
      return this.config.aliases[name];
    }
    return name;
  }

  getConfiguredPlugins(): string[] {
    return this.config?.plugins || [];
  }

  getPluginDirectories(): string[] {
    const dirs = this.config?.pluginDirs || [];

    if (this.configPath) {
      const configDir = path.dirname(this.configPath);
      return dirs.map((dir) => {
        if (path.isAbsolute(dir)) return dir;
        return path.resolve(configDir, dir);
      });
    }

    return dirs.map((dir) => path.resolve(process.cwd(), dir));
  }

  isAutoDiscoverEnabled(): boolean {
    return this.config?.autoDiscover ?? false;
  }

  async saveConfig(config: PluginConfig, filePath?: string): Promise<void> {
    const targetPath =
      filePath ||
      this.configPath ||
      path.join(process.cwd(), '.json-to-office.config.json');

    const content = JSON.stringify(config, null, 2);
    await fs.writeFile(targetPath, content, 'utf-8');

    this.config = config;
    this.configPath = targetPath;
  }

  async createDefaultConfig(filePath?: string): Promise<void> {
    const defaultConfig: PluginConfig = {
      plugins: [],
      pluginDirs: ['./plugins', './custom-components'],
      autoDiscover: false,
      aliases: {},
      theme: 'consulting',
      discovery: {
        maxDepth: 5,
        includeNodeModules: false,
      },
      validation: {
        strict: false,
        allowUnknownFields: true,
      },
    };

    await this.saveConfig(defaultConfig, filePath);
  }

  clearConfig(): void {
    this.config = null;
    this.configPath = null;
  }
}
